import { useState, useEffect } from 'react';
import api from './api';
import { useAuth } from '../context/AuthContext';

// Returns filtered colleges based on who is logged in:
// - CPUser (admin with colleges[]) → only their assigned colleges (fetched fresh from server)
// - superadmin (with colleges[]) → only their assigned colleges (fetched fresh from server)
// - collegeadmin/clerk (college field) → only their own college
// - other roles → all colleges
const useColleges = () => {
  const { user } = useAuth();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchColleges = async () => {
      setLoading(true);
      try {
        // Fetch all active colleges
        const { data } = await api.get('/controlpanel/admin/colleges', { params: { _t: Date.now() } });
        let all = (data.data || []).filter(c => c.isActive !== false);

        // For admin role, fetch fresh college assignments from server
        if (user.role === 'admin' && user.colleges) {
          try {
            const { data: adminData } = await api.get('/controlpanel/admin/me', { params: { _t: Date.now() } });
            if (adminData?.data?.colleges && adminData.data.colleges.length > 0) {
              // Preserve assignment order
              const orderedColleges = adminData.data.colleges.map(assignedCollege => 
                all.find(c => String(c._id) === String(assignedCollege._id || assignedCollege))
              ).filter(Boolean);
              all = orderedColleges;
            } else {
              all = [];
            }
          } catch {
            // Fallback to session data if API fails
            const orderedColleges = user.colleges.map(assignedCollege => 
              all.find(c => String(c._id) === String(assignedCollege._id || assignedCollege))
            ).filter(Boolean);
            all = orderedColleges;
          }
        } else if (user.role === 'superadmin' && user.colleges) {
          try {
            const { data: superAdminData } = await api.get('/controlpanel/superadmin/me', { params: { _t: Date.now() } });
            if (superAdminData?.data?.colleges && superAdminData.data.colleges.length > 0) {
              // Preserve assignment order
              const orderedColleges = superAdminData.data.colleges.map(assignedCollege => 
                all.find(c => String(c._id) === String(assignedCollege._id || assignedCollege))
              ).filter(Boolean);
              all = orderedColleges;
            } else {
              all = [];
            }
          } catch {
            // Fallback to session data if API fails
            const orderedColleges = user.colleges.map(assignedCollege => 
              all.find(c => String(c._id) === String(assignedCollege._id || assignedCollege))
            ).filter(Boolean);
            all = orderedColleges;
          }
        } else if (user?.college) {
          // For collegeadmin/clerk - filter to their own college
          const myId = String(user.college._id || user.college);
          all = all.filter(c => String(c._id) === myId);
          if (all.length === 0) all = (data.data || []).filter(c => c.isActive !== false);
        }

        setColleges(all);
      } catch (err) {
        console.error('Failed to fetch colleges:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchColleges();
  }, [user]);

  return { colleges, loading };
};

export default useColleges;
